import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gem, ShieldCheck, Truck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useFetch, api, errMsg } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { ProductCard, ProductGrid } from "../components/site/ProductCard";
import { Seo } from "../components/site/Seo";
import { imgUrl } from "../lib/shop";

const CATS = [
  { label: "Men", to: "/men", img: "/images/cat-men.webp", cls: "lg:col-span-2 lg:row-span-2" },
  { label: "Women", to: "/women", img: "/images/cat-women.webp", cls: "" },
  { label: "Sneakers", to: "/sneakers", img: "/images/cat-sneakers.webp", cls: "" },
  { label: "New Arrivals", to: "/new-arrivals", img: "/images/cat-new.webp", cls: "" },
  { label: "Sale", to: "/sale", img: "/images/cat-sale.webp", cls: "" },
];

const WHY = [
  { icon: Gem, t: "Quality Footwear", d: "Carefully selected footwear and sneaker styles." },
  { icon: ShieldCheck, t: "Secure Online Payments", d: "Safe and convenient online checkout." },
  { icon: Truck, t: "Nationwide Delivery", d: "Courier delivery across South Africa." },
  { icon: MessageCircle, t: "Customer Support", d: "Reach Mwezzy Footwear through WhatsApp or email." },
];

function Hero({ hero }) {
  return (
    <section data-testid="home-hero" className="relative min-h-[88vh] flex items-end lg:items-center overflow-hidden noise">
      <img src={imgUrl(hero?.image || "/images/hero.webp")} alt="Sneakers" className="absolute inset-0 w-full h-full object-cover object-center scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/70 to-[#0a0a0c]/20 lg:bg-gradient-to-r lg:from-[#0a0a0c] lg:via-[#0a0a0c]/75 lg:to-transparent" />
      <div className="relative container-x w-full pb-16 pt-32">
        <p className="eyebrow rise">{hero?.eyebrow}</p>
        <h1 className="font-display text-[clamp(3.25rem,18vw,6rem)] sm:text-8xl lg:text-[10rem] leading-[0.85] mt-4 rise" style={{ animationDelay: "120ms" }}>
          MWEZZY<br /><span className="text-gold-gradient">FOOTWEAR</span>
        </h1>
        <p className="font-display text-3xl sm:text-5xl tracking-[0.15em] mt-4 rise" style={{ animationDelay: "240ms" }}>STEP DIFFERENT.</p>
        <p className="text-zinc-300 max-w-md mt-4 text-sm sm:text-base rise" style={{ animationDelay: "320ms" }}>{hero?.subtitle}</p>
        <div className="flex flex-col sm:flex-row gap-3 mt-8 rise" style={{ animationDelay: "420ms" }}>
          <Link to="/shop" className="btn-gold" data-testid="hero-cta-shop">Shop Now <ArrowRight size={18} /></Link>
          <Link to="/new-arrivals" className="btn-outline" data-testid="hero-cta-new-arrivals">New Arrivals</Link>
        </div>
      </div>
    </section>
  );
}

function SectionHead({ eyebrow, title, to, testid }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-8 sm:mb-12">
      <div><p className="eyebrow">{eyebrow}</p><h2 className="font-display text-5xl sm:text-7xl mt-2 leading-none">{title}</h2></div>
      {to && <Link to={to} data-testid={testid} className="text-xs font-bold uppercase tracking-widest text-zinc-300 hover:text-[#d4af37] flex items-center gap-1 shrink-0">View all <ArrowRight size={14} /></Link>}
    </div>
  );
}

function Newsletter({ cfg }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  if (cfg && cfg.enabled === false) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/newsletter", { email, consent: true });
      toast.success(r.data.message);
      setEmail("");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section className="section border-t border-white/10">
      <div className="container-x grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <p className="eyebrow">Newsletter</p>
          <h2 className="font-display text-5xl sm:text-6xl mt-2 leading-none">{cfg?.heading || "Get first access to new drops"}</h2>
          <p className="text-zinc-400 mt-3 text-sm sm:text-base">{cfg?.text}</p>
        </div>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="field !rounded-full" data-testid="newsletter-email-input" />
          <button disabled={busy} className="btn-gold shrink-0" data-testid="newsletter-submit-button">Subscribe</button>
        </form>
        <p className="text-xs text-zinc-500 lg:col-start-2 -mt-4">By subscribing you agree to receive marketing emails. Unsubscribe anytime. See our <Link className="underline" to="/privacy">Privacy Policy</Link>.</p>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings } = useStore();
  const newDrops = useFetch("/products?collection=new&limit=8");
  const trending = useFetch("/products?sort=popular&limit=4");
  const promos = useFetch("/promotions/active");
  const promo = promos.data?.[0];
  return (
    <div>
      <Seo title={settings?.seo?.title?.replace(" | Mwezzy Footwear", "")} description={settings?.seo?.description} />
      <Hero hero={settings?.hero} />

      <section className="section" data-testid="home-new-drops">
        <div className="container-x">
          <SectionHead eyebrow="Just landed" title="New Drops" to="/new-arrivals" testid="new-drops-view-all" />
          <div className="flex gap-4 overflow-x-auto no-scrollbar snap-x -mx-4 px-4 sm:mx-0 sm:px-0">
            {(newDrops.data?.items || []).map((p, i) => (
              <div key={p.id} className="snap-start shrink-0 w-[62%] sm:w-[38%] lg:w-[23.5%]"><ProductCard p={p} index={i} /></div>
            ))}
          </div>
        </div>
      </section>

      <section className="section pt-0" data-testid="home-categories">
        <div className="container-x">
          <SectionHead eyebrow="Find your pair" title="Shop by Category" />
          <div className="grid grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-3 sm:gap-5 lg:h-[640px]">
            {CATS.map((c, i) => (
              <Link key={c.to} to={c.to} data-testid={`category-card-${c.to.slice(1)}`}
                className={`group relative overflow-hidden rounded-2xl min-h-[200px] ${i === 0 ? "col-span-2 min-h-[280px]" : ""} ${c.cls}`}>
                <img src={c.img} alt={c.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110" style={{ transition: "transform .8s cubic-bezier(.2,.7,.2,1)" }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-5 flex items-end justify-between w-full">
                  <span className="font-display text-4xl sm:text-5xl leading-none">{c.label}</span>
                  <span className="w-10 h-10 rounded-full border border-white/30 grid place-items-center group-hover:bg-[#d4af37] group-hover:text-black group-hover:border-[#d4af37]" style={{ transition: "background-color .3s, color .3s" }}><ArrowRight size={16} /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section pt-0" data-testid="home-trending">
        <div className="container-x">
          <SectionHead eyebrow="Most wanted" title="Trending Now" to="/shop?sort=popular" testid="trending-view-all" />
          <ProductGrid items={trending.data?.items || []} />
        </div>
      </section>

      {promo && (
        <section data-testid="home-promo-banner" className="relative overflow-hidden noise border-y border-[#d4af37]/30">
          {promo.image && <img src={imgUrl(promo.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0c] via-[#0a0a0c]/80 to-transparent" />
          <div className="relative container-x py-24 sm:py-36">
            <p className="eyebrow">Limited drop</p>
            <h2 className="font-display text-6xl sm:text-8xl lg:text-9xl leading-[0.85] mt-3 max-w-3xl" data-testid="promo-title">{promo.title}</h2>
            <p className="font-display text-3xl sm:text-4xl text-gold-gradient mt-3">{promo.subtitle}</p>
            <Link to={promo.cta_link || "/shop"} className="btn-gold mt-8" data-testid="promo-cta-button">{promo.cta_text} <ArrowRight size={18} /></Link>
          </div>
        </section>
      )}

      <section className="section" data-testid="home-why">
        <div className="container-x">
          <SectionHead eyebrow="The Mwezzy standard" title="Why Shop Mwezzy?" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
            {WHY.map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-[#0a0a0c] p-8 group hover:bg-[#121215]" style={{ transition: "background-color .3s" }}>
                <Icon className="text-gold group-hover:scale-110" size={32} style={{ transition: "transform .3s" }} />
                <h3 className="font-display text-3xl mt-6">{t}</h3>
                <p className="text-sm text-zinc-400 mt-2">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Newsletter cfg={settings?.newsletter} />
    </div>
  );
}
